import { useMemo, useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { User } from '@/api/entities';

/**
 * Hook to determine user tier and feature access
 * Checks subscription_tier from profile and beta_users table
 */
export const useUserTier = () => {
  const { user: authUser } = useAuth();
  const [fullUser, setFullUser] = useState(null);
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [isLoadingBeta, setIsLoadingBeta] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Load full user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!authUser) {
        setFullUser(null);
        setIsLoadingProfile(false);
        return;
      }

      try {
        const profile = await User.me();
        setFullUser(profile);
      } catch (error) {
        console.error('Error loading user profile:', error);
        // Fallback to auth user if profile load fails
        setFullUser(authUser);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadUserProfile();
  }, [authUser]);

  // Check beta status from beta_users table
  useEffect(() => {
    const checkBetaStatus = async () => {
      if (!fullUser?.email) {
        setIsLoadingBeta(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('beta_users')
          .select('status')
          .eq('email', fullUser.email.toLowerCase())
          .maybeSingle();

        if (!error && data?.status === 'approved') {
          setIsBetaUser(true);
        } else {
          setIsBetaUser(false);
        }
      } catch (error) {
        console.debug('Could not check beta_users table:', error.message);
        setIsBetaUser(false);
      } finally {
        setIsLoadingBeta(false);
      }
    };

    checkBetaStatus();
  }, [fullUser?.email]);

  // Determine user tier
  // Priority: beta > premium > free
  const tier = useMemo(() => {
    if (!fullUser) return 'free';
    
    // Check beta status first
    if (isBetaUser) return 'beta';
    
    // Check subscription_tier from profile
    if (fullUser.subscription_tier === 'premium' || fullUser.subscription_tier === 'beta') {
      return fullUser.subscription_tier;
    }
    
    // Default to free
    return 'free';
  }, [fullUser, isBetaUser]);

  // Feature flags - Enable premium features for all users
  const features = useMemo(() => ({
    hasCalendarIntegration: true,  // Enable for all users
    hasSummerCoverage: true,       // Enable for all users
    hasAdvancedViews: true,        // Enable for all users
    hasMultiChild: true,           // Enable for all users
    isBeta: tier === 'beta',
    isPremium: tier === 'premium',
    isFree: tier === 'free'
  }), [tier]);

  // Calendar status - check both google_calendar_connected and google_calendar_token
  const isCalendarConnected = useMemo(() => {
    return !!(fullUser?.google_calendar_connected || fullUser?.google_calendar_token);
  }, [fullUser]);

  return {
    tier,
    features,
    isCalendarConnected,
    user: fullUser,
    isLoadingBeta,
    isLoadingProfile
  };
};

